/** @jsxImportSource ./simboluri-jsx */
import Info from "./Info";
import { numar, useT } from "../i18n";
import type { Cheie } from "../i18n";
import { CULOARE_CAZ, Mod3D } from "./Incarcari3D";
import { ETICHETA_TIP } from "./LegendaCazuri";
import { AvertismentMetoda, CazLegenda, MasaNivel } from "./tipuri";

// FIȘA LAYERULUI SELECTAT în tabul «Încărcări» al Studioului.
//
// Scena arată UNDE cade încărcarea; fișa spune CE e, din ce cifră iese, pe ce
// articol de normativ stă, cât cade pe fiecare planșeu și ce anume NU e în
// model. Textele nu sunt scrise aici: vin din `incarcari.legenda_cazuri` și
// `incarcari.limite_metoda` — aceleași cu cele de la Pasul 8 și Pasul 11, ca
// un simbol să nu fie explicat în două feluri.
//
// Forma e cea a drawer-ului de element (`ob-studio-inspector`): capul pe
// fundalul aplicației, secțiuni despărțite de linii, eticheta fiecăreia mono.

const G = 9.80665;

function hex(caz: string): string {
  return `#${(CULOARE_CAZ[caz] ?? 0x7a8ca3).toString(16).padStart(6, "0")}`;
}

function nr(v: number | null | undefined, d = 0): string {
  return v == null ? "—" : numar(v, {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });
}

function Sectiune({
  eticheta, children, clasa,
}: {
  eticheta: string;
  children: React.ReactNode;
  clasa?: string;
}) {
  return (
    <div className={`ob-fisa-sec${clasa ? ` ${clasa}` : ""}`}>
      <div className="ob-lbl">{eticheta}</div>
      {children}
    </div>
  );
}

/** Capul fișei: simbolul, în culoarea cu care e desenat în scenă. */
function Cap({
  simbol, titlu, subtitlu, culoare, chipuri, onInchide,
}: {
  simbol: string;
  titlu: string;
  subtitlu: string;
  culoare: string;
  chipuri: React.ReactNode;
  onInchide: () => void;
}) {
  const t = useT();
  return (
    <div className="ob-studio-sumar">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          className="sg-mono"
          style={{
            background: culoare, color: "#fff", borderRadius: 7,
            padding: "3px 9px", fontSize: 14, flex: "none",
          }}
        >
          {simbol}
        </span>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="ob-disp" style={{ fontSize: 17, fontWeight: 600, color: "var(--ink)" }}>
            {titlu}
          </div>
          <div className="sg-muted" style={{ fontSize: 12 }}>{subtitlu}</div>
        </div>
        <button className="ob-studio-inchide" onClick={onInchide} title={t("struct.fisai.inchide")}>
          ×
        </button>
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 7 }}>
        {chipuri}
      </div>
    </div>
  );
}

/** Cât din layer cade pe fiecare planșeu — bara e proporția din maximul lui. */
function PeNiveluri({
  randuri, culoare, unitate,
}: {
  randuri: { nivel: number; nume: string | null; val: number; nota?: string }[];
  culoare: string;
  unitate: string;
}) {
  const t = useT();
  const max = Math.max(...randuri.map((x) => x.val), 1e-9);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {[...randuri].reverse().map((x) => (
        <div className="ob-fisa-rand" key={x.nivel}>
          <span className="ob-fisa-niv" title={x.nume ?? ""}>
            {x.nume ?? t("struct.fisai.nivel_n", { n: x.nivel })}
          </span>
          <span className="ob-fisa-bara">
            <span style={{
              width: `${Math.max(100 * x.val / max, x.val > 0 ? 2 : 0)}%`,
              background: x.val > 0 ? culoare : undefined,
            }} />
          </span>
          <span className="ob-fisa-kn" title={x.nota}>
            {x.val > 0 ? `${nr(x.val)} ${unitate}` : "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Divergențele de metodă care privesc cazul (Pasul 8, `limite_metoda`). */
function Limite({ limite }: { limite: AvertismentMetoda[] }) {
  const t = useT();
  return (
    <>
      {limite.map((l) => (
        <div
          key={l.cod}
          className={`ob-fisa-sec ob-fisa-nota${l.severitate === "info" ? " info" : ""}`}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span className={`ob-chip ${l.severitate === "info" ? "ob-c-mut" : "ob-c-amb"}`}>
              {l.cod}
            </span>
            <span className="ob-lbl" style={{
              color: l.severitate === "info" ? "var(--mut)" : "var(--amb)",
            }}>
              {t(l.severitate === "info" ? "struct.fisai.info" : "struct.fisai.lipsa")}
            </span>
          </div>
          <div className="ob-fisa-text" style={{ fontWeight: 500 }}>{l.titlu}</div>
          <div className="ob-fisa-text">{l.impact}</div>
          <div className="sg-muted" style={{ fontSize: 12 }}>{l.normativ}</div>
        </div>
      ))}
    </>
  );
}

export default function FisaIncarcare({
  mod,
  legenda,
  limite,
  mase,
  masaInfraT,
  linkPas8,
  linkSeism,
  onInchide,
}: {
  mod: Mod3D;
  legenda: CazLegenda[];
  limite: AvertismentMetoda[];
  mase: MasaNivel[];
  masaInfraT?: number | null;
  linkPas8: string;
  linkSeism: string;
  onInchide: () => void;
}) {
  const t = useT();
  if (mod.fel === "masa") {
    return (
      <FisaMasa mase={mase} masaInfraT={masaInfraT} linkSeism={linkSeism}
                onInchide={onInchide} />
    );
  }
  const l = legenda.find((x) => x.caz === mod.caz);
  if (!l) return null;
  const ale = limite.filter((x) => (x.cazuri ?? []).includes(l.caz));
  const culoare = hex(l.caz);
  const expus = (l.pe_nivel ?? []).some((n) => (n.arie_expusa_m2 ?? 0) > 0)
    && l.caz === "S";

  return (
    <aside className="ob-studio-inspector">
      <Cap
        simbol={l.caz}
        titlu={l.nume}
        subtitlu={t("struct.fisai.subtitlu")}
        culoare={culoare}
        onInchide={onInchide}
        chipuri={
          <>
            <span className={`ob-chip ${
              l.tip === "permanenta" ? "ob-c-mut" : "ob-c-amb"}`}>
              {ETICHETA_TIP[l.tip] ? t(ETICHETA_TIP[l.tip]) : l.tip}
            </span>
            {l.psi0 != null && (
              <span className="ob-chip ob-c-mut">
                ψ₀ {nr(l.psi0, 1)} · ψ₂ {nr(l.psi2, 1)}
              </span>
            )}
          </>
        }
      />

      <Sectiune eticheta={t("struct.fisai.total")}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <span className="ob-disp" style={{ fontSize: 26, fontWeight: 600, color: "var(--ink)" }}>
            {nr(l.total_kn)}
          </span>
          <span className="sg-muted" style={{ fontSize: 13 }}>{t("struct.fisai.kn_cladire")}</span>
        </div>
      </Sectiune>

      {l.unitar && (
        <Sectiune eticheta={t("struct.fisai.cum")}>
          <div className="ob-fisa-formula">{l.unitar}</div>
          {l.sursa && <div className="sg-muted" style={{ fontSize: 12 }}>{l.sursa}</div>}
        </Sectiune>
      )}

      {l.unde && (
        <Sectiune eticheta={t("struct.fisai.unde")}>
          <div className="ob-fisa-text">{l.unde}</div>
          {expus && (
            <div className="sg-muted" style={{ fontSize: 12 }}>
              {t("struct.fisai.expus")}
            </div>
          )}
        </Sectiune>
      )}

      {!!l.pe_nivel?.length && (
        <Sectiune eticheta={t("struct.fisai.pe_planse")}>
          <PeNiveluri
            culoare={culoare}
            unitate="kN"
            randuri={l.pe_nivel.map((n) => ({
              nivel: n.nivel, nume: n.nume, val: n.kn,
              nota: expus
                ? t("struct.fisai.nota_expus", {
                    expusa: nr(n.arie_expusa_m2, 0), total: nr(n.arie_m2, 0),
                  })
                : t("struct.fisai.nota_planseu", { n: nr(n.arie_m2, 0) }),
            }))}
          />
          <div className="sg-muted" style={{ fontSize: 12 }}>
            {t("struct.fisai.opensees")}
          </div>
        </Sectiune>
      )}

      <Limite limite={ale} />

      <div className="ob-studio-actiuni" style={{
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        marginTop: "auto",
      }}>
        {l.normativ && <span className="ob-ref">{l.normativ}</span>}
        <span style={{ flex: "1 1 auto" }} />
        <a className="ob-sari" href={linkPas8}>{t("struct.fisai.link_p8")}</a>
      </div>
    </aside>
  );
}

/** Masa seismică: a doua contabilitate a aceleiași clădiri. */
function FisaMasa({
  mase, masaInfraT, linkSeism, onInchide,
}: {
  mase: MasaNivel[];
  masaInfraT?: number | null;
  linkSeism: string;
  onInchide: () => void;
}) {
  const t = useT();
  const total = mase.reduce((a, m) => a + (m.total_t ?? 0), 0);
  const compus: [Cheie, number][] = [
    ["struct.masa.c_structura", mase.reduce(
      (a, m) => a + (m.placa_t ?? 0) + (m.grinzi_t ?? 0) + (m.stalpi_t ?? 0), 0)],
    ["struct.masa.c_finisaje", mase.reduce((a, m) => a + (m.finisaje_t ?? 0), 0)],
    ["struct.masa.c_pereti", mase.reduce(
      (a, m) => a + (m.pereti_grinzi_t ?? 0) + (m.pereti_placa_t ?? 0), 0)],
    ["struct.masa.c_utila", mase.reduce((a, m) => a + (m.utila_t ?? 0), 0)],
    ["struct.masa.c_zapada", mase.reduce((a, m) => a + (m.zapada_t ?? 0), 0)],
  ];
  const max = Math.max(...compus.map(([, v]) => v), 1e-9);

  return (
    <aside className="ob-studio-inspector">
      <Cap
        simbol="m"
        titlu={t("struct.masa.titlu")}
        subtitlu={t("struct.masa.subtitlu")}
        culoare="var(--met)"
        onInchide={onInchide}
        chipuri={
          <>
            <span className="ob-chip ob-c-met">{t("struct.masa.chip_nod")}</span>
            <span className="ob-chip ob-c-mut">{t("struct.masa.chip_nu_incarcare")}</span>
          </>
        }
      />

      <Sectiune eticheta={t("struct.masa.totala")}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <span className="ob-disp" style={{ fontSize: 26, fontWeight: 600, color: "var(--ink)" }}>
            {nr(total)}
          </span>
          <span className="sg-muted" style={{ fontSize: 13 }}>
            t · {nr(total * G)} kN
          </span>
          <Info
            titlu={t("struct.masa.titlu")}
            ce={t("struct.masa.info_ce")}
            deCe={t("struct.masa.info_de_ce")}
          />
        </div>
        {masaInfraT != null && (
          <div className="sg-muted" style={{ fontSize: 12 }}>
            {t("struct.masa.infra", { n: nr(masaInfraT) })}
          </div>
        )}
      </Sectiune>

      <Sectiune eticheta={t("struct.masa.compus")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {compus.filter(([, v]) => v > 0).map(([nume, v]) => (
            <div className="ob-fisa-rand" key={nume}>
              <span className="ob-fisa-bara" style={{ flex: "0 0 74px" }}>
                <span style={{ width: `${100 * v / max}%`, background: "var(--met)" }} />
              </span>
              <span style={{ flex: "1 1 auto", fontSize: 12 }}>{t(nume)}</span>
              <span className="ob-fisa-kn">{nr(v)} t</span>
            </div>
          ))}
        </div>
      </Sectiune>

      {!!mase.length && (
        <Sectiune eticheta={t("struct.fisai.pe_planse")}>
          <PeNiveluri
            culoare="var(--met)"
            unitate="t"
            randuri={mase.map((m) => ({
              nivel: m.nivel_diafragma ?? 0, nume: m.nivel ?? null, val: m.total_t ?? 0,
              nota: m.zapada_t
                ? t("struct.masa.nota_zapada", { n: nr(m.zapada_t) })
                : undefined,
            }))}
          />
        </Sectiune>
      )}

      <div className="ob-studio-actiuni" style={{
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        marginTop: "auto",
      }}>
        <span className="ob-ref">P100-1/2013 §4.5.3</span>
        <span style={{ flex: "1 1 auto" }} />
        <a className="ob-sari" href={linkSeism}>{t("struct.masa.link_p9")}</a>
      </div>
    </aside>
  );
}
